import io from "socket.io-client";

const accessToken = () => {
  const raw = localStorage.getItem("token");
  if (!raw) return "";
  try { return JSON.parse(raw); } catch (_) { return raw; }
};

// ─────────────────────────────────────────────────────────────────────────────
// Contrato deste worker:
//
//   • O socket é UM singleton, sempre ativo, reutilizado por todos os
//     componentes. Não recriamos a instância.
//   • O FRONTEND NUNCA DESCONECTA. disconnect() é no-op (warn). Apenas o
//     backend decide encerrar a conexão (via pingInterval/pingTimeout do
//     engine.io ou desconexão deliberada).
//   • Qualquer queda (transport close, ping timeout, io server disconnect,
//     io client disconnect, etc.) dispara reconexão automática.
//   • off(event[, callback]) emite console.warn sempre — auditoria de
//     handlers, já que removê-los pode comprometer features se mal usado.
//   • Listeners do usuário sobrevivem a reconexões automaticamente
//     (socket.io-client mantém eles atrelados ao mesmo Socket instance).
// ─────────────────────────────────────────────────────────────────────────────

class SocketWorker {
  constructor() {
    this.socket = null;
    this.companyId = null;
    this.userId = null;
    this.eventListeners = {};
    this.rooms = new Set();
    this.pendingEvents = [];
    this._internalAttached = false;

    if (typeof window !== "undefined") {
      // Voltou ao foco / rede voltou → se estiver desconectado por qualquer
      // motivo, força reconexão imediata. Não tocamos no transport quando
      // está conectado — o backend é quem valida liveness via ping nativo
      // do engine.io.
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          this._reconnectIfDown();
        }
      });
      window.addEventListener("online", () => {
        this._reconnectIfDown();
      });
    }
  }

  configureSocket(companyId) {
    if (!companyId) return;
    if (this.socket) return; // singleton — nunca recriamos

    this.companyId = companyId;
    this.socket = io(process.env.REACT_APP_BACKEND_URL, {
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      transports: ["websocket"],
      timeout: 10000,
      auth: { token: accessToken() },
    });

    this._attachInternalListeners();
  }

  _attachInternalListeners() {
    if (!this.socket || this._internalAttached) return;
    this._internalAttached = true;

    this.socket.on("connect", () => {
      console.log("[socket] conectado");

      // Re-entra nas salas (Set evita duplicação a cada reconexão)
      this.rooms.forEach((room) => this.socket.emit("join", room));

      // Aplica eventos enfileirados antes da primeira conexão
      if (this.pendingEvents.length > 0) {
        const queue = this.pendingEvents.splice(0);
        queue.forEach(({ event, wrapped }) => this.socket.on(event, wrapped));
      }

      if (this.companyId) {
        this.joinRoom(`company-${this.companyId}-room`);
      }
    });

    this.socket.on("disconnect", (reason) => {
      console.warn(
        "[socket] desconectado:",
        reason,
        "— frontend reconecta sempre. Apenas o backend encerra de fato."
      );
      // socket.io-client auto-reconecta para: transport close, transport
      // error, ping timeout. Para 'io server disconnect' e
      // 'io client disconnect' precisamos reconectar manualmente.
      if (
        reason === "io server disconnect" ||
        reason === "io client disconnect"
      ) {
        setTimeout(() => {
          if (this.socket && !this.socket.connected) {
            try {
              this.socket.connect();
            } catch (e) {
              // ignora — próximo trigger (visibility/online) tenta de novo
            }
          }
        }, 500);
      }
    });

    this.socket.on("connect_error", (err) => {
      console.warn("[socket] connect_error:", err?.message || err);
      // A lib já tenta de novo automaticamente (reconnection: true)
    });

    this.socket.on("reconnect_failed", () => {
      // Com reconnectionAttempts: Infinity isso teoricamente não dispara,
      // mas se em algum cenário disparar, force um connect manual.
      console.warn(
        "[socket] reconnect_failed — forçando reconexão manual"
      );
      try {
        this.socket.connect();
      } catch (e) {
        // ignora
      }
    });

    // Liveness em nível de APLICAÇÃO. O backend pergunta "presence:ping" de tempos
    // em tempos; respondemos o ack pra provar que o JS está vivo (aba ativa, não
    // congelada). Sem essa resposta o backend marca o usuário offline — é o que
    // diferencia "aba realmente viva" de "TCP pendurado por proxy/notebook dormindo".
    this.socket.on("presence:ping", (ack) => {
      if (typeof ack === "function") ack();
    });

    // app_version registrado uma única vez (antes era anexado dentro do
    // handler de 'connect' e duplicava a cada reconexão)
    this.socket.on("app_version", (data) => {
      console.log("📦 Socket: Recebido evento de versão", data);

      if (!data?.version) return;

      const storedVersion = localStorage.getItem("appVersion");

      if (process.env.NODE_ENV === "development" || data.version === "dev") {
        return;
      }

      if (storedVersion && data.version !== storedVersion) {
        console.log(
          "🔄 Socket: Versão desatualizada detectada (Local: " +
            storedVersion +
            ", Remote: " +
            data.version +
            "), forçando reload..."
        );

        localStorage.setItem("appVersion", data.version);

        if ("caches" in window) {
          caches.keys().then((names) => {
            names.forEach((name) => caches.delete(name));
          });
        }
        sessionStorage.clear();

        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.getRegistrations().then((registrations) => {
            for (let registration of registrations) {
              registration.unregister();
            }
          });
        }

        setTimeout(() => {
          window.location.reload(true);
        }, 1000);
      } else if (!storedVersion) {
        localStorage.setItem("appVersion", data.version);
      }
    });
  }

  _reconnectIfDown() {
    if (this.socket && !this.socket.connected) {
      try {
        this.socket.connect();
      } catch (e) {
        // ignora
      }
    }
  }

  // ───────── on / off ─────────

  on(event, callback, room = null) {
    const wrapped = room
      ? (data) => {
          if (data?.room === room) {
            callback(data);
          }
        }
      : callback;

    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push({ original: callback, wrapped, room });

    if (this.socket) {
      this.socket.on(event, wrapped);
    } else {
      this.pendingEvents.push({ event, wrapped });
    }
  }

  // off() sempre warna — auditoria. Remover handler em hora errada pode
  // matar features inteiras (a tela para de atualizar mas o socket continua
  // vivo, sem nenhum sintoma de erro). Logamos pra rastrear.
  off(event, callback) {
    if (callback === undefined) {
      console.warn(
        `[socket] .off('${event}') — TODOS os handlers deste evento foram removidos`
      );
      if (this.socket) this.socket.off(event);
      this.pendingEvents = this.pendingEvents.filter((p) => p.event !== event);
      delete this.eventListeners[event];
      return;
    }

    const listeners = this.eventListeners[event];
    if (!listeners) {
      console.warn(
        `[socket] .off('${event}', <callback>) — nenhum handler registrado pra esse evento (no-op)`
      );
      return;
    }

    const idx = listeners.findIndex((l) => l.original === callback);
    if (idx === -1) {
      console.warn(
        `[socket] .off('${event}', <callback>) — callback informado não está registrado (no-op)`
      );
      return;
    }

    console.warn(`[socket] .off('${event}', <callback>) — handler removido`);

    const { wrapped } = listeners[idx];
    listeners.splice(idx, 1);
    if (this.socket) this.socket.off(event, wrapped);
    this.pendingEvents = this.pendingEvents.filter((p) => p.wrapped !== wrapped);

    if (listeners.length === 0) {
      delete this.eventListeners[event];
    }
  }

  // ───────── rooms / emit ─────────

  joinRoom(room) {
    if (this.socket && this.socket.connected) {
      this.socket.emit("join", room);
    }
    this.rooms.add(room);
  }

  leaveRoom(room) {
    if (this.socket && this.socket.connected) {
      this.socket.emit("leave", room);
    }
    this.rooms.delete(room);
  }

  emit(event, data, ack) {
    if (this.socket) {
      if (typeof ack === "function") this.socket.emit(event, data, ack);
      else this.socket.emit(event, data);
    } else if (typeof ack === "function") {
      ack({ error: "socket_not_configured" });
    }
  }

  emitToRoom(room, event, data) {
    if (this.socket) {
      this.socket.emit(event, { room, data });
    }
  }

  // ───────── ciclo de vida ─────────

  // connect(companyId) ou connect(companyId, userId).
  // Idempotente: pode ser chamado quantas vezes quiser; só (re)configura
  // socket se ainda não existir e só dispara connect() se desconectado.
  connect(companyId, userId) {
    // Troca de empresa (login em conta diferente) — sai da sala anterior
    if (companyId && this.companyId && companyId !== this.companyId) {
      this.leaveRoom(`company-${this.companyId}-room`);
    }
    if (companyId) this.companyId = companyId;
    if (userId) this.userId = userId;

    if (!this.socket && companyId) {
      this.configureSocket(companyId);
    }
    const nextToken = accessToken();
    const principalChanged = this.socket && this.socket.auth?.token !== nextToken;
    if (this.socket) this.socket.auth = { token: nextToken };
    if (principalChanged && this.socket.connected) {
      this.socket.disconnect().connect();
      return;
    }

    if (this.socket && !this.socket.connected) {
      this.socket.connect();
    }
  }

  setUserId(userId) {
    if (userId) this.userId = userId;
  }

  // No-op intencional. Frontend NUNCA desconecta — quem decide encerrar é
  // o backend (via engine.io ping/timeout ou desconexão deliberada do
  // servidor). Mantemos a assinatura pra não quebrar callers legados, mas
  // logamos warn pra rastrear quem está chamando.
  //
  // Se a chamada vier justamente de um caminho onde o socket caiu (raro,
  // mas defensivo), aproveitamos pra disparar reconexão.
  disconnect() {
    console.warn(
      "[socket] disconnect() chamado no frontend — IGNORADO. Frontend não desconecta; apenas o backend encerra a conexão. Verifique se essa chamada é necessária."
    );
    this._reconnectIfDown();
  }
}

const socket = new SocketWorker();

export default socket;
